import { useMemo } from 'react'
import { FormProvider, useController, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'

import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import usePatientConsents from '@shared/hooks/src/usePatientConsents'
import useQuery from '@shared/hooks/src/useQuery'
import { useMe } from '@shared/providers/src/MeProvider'
import API from '@shared/services/src/API'
import { handleError, includesOneOfErrorMessages, UserSettingName } from '@shared/utils'

import useFormatMessage from '@hooks/useFormatMessage'
import { styles as dialogStyles } from '@components/Dialog/Dialog.utils'
import HookInputControl from '@components/HookInputControl'

import { useGiveConsent } from './ConsentDialog.hooks'

export default function ConsentDialog() {
  const me = useMe()
  const formatMessage = useFormatMessage()
  const { data: requiredConsents } = usePatientConsents(me.id, { all: false })

  const giveConsent = useGiveConsent()

  const consentToTreatRequired = requiredConsents.some((c) => c.name === UserSettingName.Initials)
  const agreeToShareDataWithCBORequired = requiredConsents.some((c) => c.name === UserSettingName.AllowSharingHealthData)
  const agreeToTermsOfServiceRequired = requiredConsents.some((c) => c.name === UserSettingName.AcceptTermsOfService)
  const agreeToPrivacyPolicyRequired = requiredConsents.some((c) => c.name === UserSettingName.AcceptPrivacyPolicy)
  const agreeToTelehealthConsentRequired = requiredConsents.some((c) => c.name === UserSettingName.AcceptTelehealthConsent)
  const policiesRequired = agreeToTermsOfServiceRequired || agreeToPrivacyPolicyRequired || agreeToTelehealthConsentRequired

  const form = useForm({
    mode: 'all',
    resolver: yupResolver(
      Yup.object().shape({
        ...(consentToTreatRequired && {
          initials: Yup.string().trim().required(formatMessage('consent.treatment.error.required')),
        }),
        ...(agreeToShareDataWithCBORequired && {
          sharing_data: Yup.boolean()
            .required(formatMessage('consent.dataSharing.error.required'))
            .oneOf([true], formatMessage('consent.dataSharing.error.required')),
        }),
        ...(policiesRequired && {
          agree: Yup.boolean()
            .required(formatMessage('consent.policyUpdates.error.required'))
            .oneOf([true], formatMessage('consent.policyUpdates.error.required')),
        }),
      })
    ),
    defaultValues: {
      ...(consentToTreatRequired && { initials: '' }),
      ...(agreeToShareDataWithCBORequired && { sharing_data: false }),
      ...(policiesRequired && { agree: false }),
    },
  })

  const onSubmit = (values) => {
    return giveConsent
      .mutateAsync({
        ...(consentToTreatRequired && { initials: values.initials }),
        ...(agreeToShareDataWithCBORequired && { sharing_data: values.sharing_data }),
        ...(agreeToTermsOfServiceRequired && { agree_tos: values.agree }),
        ...(agreeToPrivacyPolicyRequired && { agree_privacy_policy: values.agree }),
        ...(agreeToTelehealthConsentRequired && { telehealth_consent: values.agree }),
      })
      .catch((response) => {
        if (includesOneOfErrorMessages(response.error, ['initials'])) {
          return form.setError('initials', { message: formatMessage('consent.treatment.error.required') })
        }
        handleError(response)
      })
  }

  return (
    <Dialog open fullWidth maxWidth="sm" aria-labelledby="consent-dialog-title">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} aria-describedby="consent-dialog-description">
          <DialogContent sx={dialogStyles.content}>
            <Stack spacing={4} direction="column">
              {consentToTreatRequired && <Consent />}
              {agreeToShareDataWithCBORequired && <DataSharing />}
              {policiesRequired && (
                <PolicyLinks
                  requiredConsents={{
                    agreeToTermsOfServiceRequired,
                    agreeToPrivacyPolicyRequired,
                    agreeToTelehealthConsentRequired,
                  }}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={dialogStyles.actions}>
            <Button
              {...dialogStyles.button}
              variant="contained"
              type="submit"
              loading={form.formState.isSubmitting}
              disabled={!form.formState.isDirty}
            >
              {formatMessage('common.save')}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

function Consent() {
  const formatMessage = useFormatMessage()

  return (
    <Stack spacing={1} role="group" aria-labelledby="consent-to-treat-heading" sx={{ alignItems: 'start' }}>
      <Typography variant="h6" id="consent-to-treat-heading">
        {formatMessage('consent.treatment.title')}
      </Typography>
      <Typography id="consent-to-treat-description">{formatMessage('consent.treatment.description')}</Typography>
      <Typography>{formatMessage('consent.treatment.instructions')}</Typography>
      <HookInputControl field="initials">
        <TextField label={formatMessage('form.initials.label')} placeholder={formatMessage('form.initials.placeholder')} />
      </HookInputControl>
    </Stack>
  )
}

function DataSharing() {
  const { cbo } = useMe()
  const formatMessage = useFormatMessage()
  const { field, fieldState } = useController({ name: 'sharing_data' })

  return (
    <Stack spacing={1} role="group" aria-labelledby="data-sharing-heading">
      <Typography variant="h6" id="data-sharing-heading">
        {formatMessage('consent.dataSharing.title')}
      </Typography>
      <Typography>{formatMessage('consent.dataSharing.description', { cboName: cbo?.name })}</Typography>
      <Typography variant="body2">{formatMessage('consent.dataSharing.note')}</Typography>
      <Stack>
        <FormControlLabel
          name="sharing_data"
          label={formatMessage('consent.dataSharing.agreement', {
            cboName: cbo?.name || formatMessage('consent.dataSharing.defaultPartner'),
          })}
          control={<Checkbox checked={field.value} onChange={field.onChange} />}
        />
        {fieldState.error && <FormHelperText error>{fieldState.error?.message}</FormHelperText>}
      </Stack>
    </Stack>
  )
}

function PolicyLinks({ requiredConsents }) {
  const formatMessage = useFormatMessage()
  const { field, fieldState } = useController({ name: 'agree' })

  return (
    <Stack spacing={1} role="group" aria-labelledby="policy-updates-heading">
      <Typography variant="h6" id="policy-updates-heading">
        {formatMessage('consent.policyUpdates.title')}
      </Typography>
      <Typography>{formatMessage('consent.policyUpdates.label')}</Typography>
      <Stack>
        <FormControlLabel
          label={<PolicyLabel requiredConsents={requiredConsents} />}
          control={<Checkbox checked={field.value} onChange={field.onChange} />}
        />
        {fieldState.error && <FormHelperText error>{fieldState.error?.message}</FormHelperText>}
      </Stack>
    </Stack>
  )
}

function PolicyLabel({ requiredConsents }) {
  const formatMessage = useFormatMessage()
  const { data } = useQuery({ queryKey: ['consent-documents'], queryFn: () => API.me.consents() })

  const content = useMemo(() => {
    const links = [
      requiredConsents.agreeToTelehealthConsentRequired && (
        <Link key="telehealth" component="a" href={data?.['telehealth_consent'] || '#'} target="_blank" rel="noopener noreferrer">
          {formatMessage('enrollment.consent.telehealthConsent')}
        </Link>
      ),
      requiredConsents.agreeToPrivacyPolicyRequired && (
        <Link key="privacy" component="a" href={data?.['agree_privacy_policy'] || '#'} target="_blank" rel="noopener noreferrer">
          {formatMessage('enrollment.consent.privacyPolicy')}
        </Link>
      ),
      requiredConsents.agreeToTermsOfServiceRequired && (
        <Link key="tos" component="a" href={data?.['agree_tos'] || '#'} target="_blank" rel="noopener noreferrer">
          {formatMessage('enrollment.consent.termsOfService')}
        </Link>
      ),
    ].filter(Boolean)

    return links.map((node, index) => {
      if (index === links.length - 2) {
        return (
          <span key={index}>
            {node} {formatMessage('common.conjunction.and')}{' '}
          </span>
        )
      } else if (index < links.length - 2) {
        return (
          <span key={index}>
            {node}
            {formatMessage('common.conjunction.comma')}{' '}
          </span>
        )
      }
      return <span key={index}>{node}</span>
    })
  }, [data, requiredConsents, formatMessage])

  return (
    <Typography variant="body2">
      {formatMessage('consent.policyUpdates.accept')} {content}
    </Typography>
  )
}
